import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, Maximize2, Play, Pause } from 'lucide-react';

const slides = [
  { src: '/netrakshak/slide-1.png', label: 'Safe & controlled internet for BSNL customers',
    caption: 'A BSNL customer digital-safety initiative for FTTH networks, powered by Mashmari.',
    alt: 'NetRakshak title slide: Enabling Safe & Controlled Internet for BSNL Customers — a Customer Digital Safety Initiative for FTTH Networks, powered by Mashmari Leapfrog Learning Program, with BSNL branding' },
  { src: '/netrakshak/slide-2.png', label: 'Unmet need in BSNL households',
    caption: 'No home-level control today — parents are left helpless and demand for protection is rising.',
    alt: 'Slide on unmet need in BSNL households: no home-level control, parents left helpless, no network protection and growing demand for safe digital environments' },
  { src: '/netrakshak/slide-3.png', label: 'Unmet need for millions of subscribers',
    caption: 'Offices, shops, malls and restaurants have no simple way to control shared access.',
    alt: 'Slide on unmet need for BSNL subscribers: no SOHO/office-level control, common access for shop-floor users, no guest offering for malls and restaurants, and growing demand' },
  { src: '/netrakshak/slide-4.png', label: 'Customer-level internet control',
    caption: 'BSNL gives visibility of every home device and restricted vs unrestricted access per device.',
    alt: 'Slide showing BSNL customer-level control: visibility of all home devices and per-device access — child/employee devices get restricted domains, parent/admin devices get unrestricted internet' },
  { src: '/netrakshak/slide-5.png', label: 'Network-integrated control architecture',
    caption: 'Control sits at the BSNL router/ONT layer — app-independent and uniform across devices.',
    alt: 'Slide on network-integrated control architecture: control implemented at the BSNL router/ONT layer, working at network level independent of apps, for uniform control across connected devices' },
  { src: '/netrakshak/slide-6.png', label: 'A new customer service opportunity',
    caption: 'A premium FTTH value-added service: first-mover advantage, new revenue and brand lift.',
    alt: 'Slide on the customer service opportunity: go-to-market as a premium FTTH value-added service, first-mover advantage, a new revenue stream and stronger brand positioning' },
  { src: '/netrakshak/slide-7.png', label: 'Strategic value and next steps',
    caption: 'A cybersecurity baseline, CPE standardisation and a five-working-day launch-readiness plan.',
    alt: 'Slide on strategic value and next steps: cybersecurity baseline, CPE standardisation, future-ready foundation and scale readiness, with a five-working-day launch-readiness plan' },
];

const INTERVAL = 12000;

export default function NetRakshakSlideshow() {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const count = slides.length;
  const go = useCallback((next) => setIndex((i) => (next + count) % count), [count]);
  const current = slides[index];
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const autoplay = playing && !lightbox && !reduceMotion;

  useEffect(() => { setZoomed(false); }, [index]);

  useEffect(() => {
    if (!autoplay) return;
    const t = setTimeout(() => go(index + 1), INTERVAL);
    return () => clearTimeout(t);
  }, [index, autoplay, go]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight') go(index + 1);
      if (e.key === 'ArrowLeft') go(index - 1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [lightbox, index, go]);

  const openLightbox = () => { setZoomed(false); setLightbox(true); };

  return (
    <div data-testid="netrak-slideshow">
      {/* Full, uncropped 16:9 slide sized to fit the viewport — no overlays on the artwork */}
      <div className="relative rounded-2xl border border-[var(--mm-line)] bg-white overflow-hidden shadow-[0_24px_60px_-40px_rgba(74,18,89,.4)]">
        <button
          type="button"
          onClick={openLightbox}
          aria-label={`Enlarge slide: ${current.label}`}
          data-testid="netrak-open-lightbox"
          className="block w-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]"
        >
          <span className="flex items-center justify-center bg-white" style={{ height: 'min(50vh, 56.25vw)' }}>
            <img
              key={current.src}
              src={current.src}
              alt={current.alt}
              data-testid={`netrak-image-${index}`}
              className="max-w-full max-h-full object-contain"
            />
          </span>
        </button>
        {/* Thin ~12s countdown bar — sits below the artwork, does not cover it */}
        <div className="h-1 w-full bg-[var(--mm-line)]" aria-hidden="true" data-testid="netrak-progress-track">
          {!reduceMotion && (
            <div
              key={`${index}-${playing}`}
              data-testid="netrak-progress-bar"
              className="h-full bg-[var(--mm-purple)]"
              style={{
                width: playing ? '0%' : '0%',
                animation: `mmSlideProgress ${INTERVAL}ms linear forwards`,
                animationPlayState: autoplay ? 'running' : 'paused',
              }}
            />
          )}
        </div>
      </div>

      {/* Optional caption strip for skimmers — beneath the slide */}
      <p className="mt-3 text-sm text-[var(--mm-ink)]/70 leading-relaxed" data-testid="netrak-caption">
        <span className="font-semibold text-[var(--mm-ink)]">{current.label}.</span> {current.caption}
      </p>

      {/* Controls */}
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <button onClick={() => go(index - 1)} aria-label="Previous slide" data-testid="netrak-prev"
          className="w-10 h-10 rounded-full border border-[var(--mm-line)] bg-white text-[var(--mm-purple)] flex items-center justify-center hover:border-[var(--mm-purple)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={() => setPlaying((p) => !p)} aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
          aria-pressed={playing} data-testid="netrak-playpause"
          className="w-10 h-10 rounded-full bg-[var(--mm-purple)] text-white flex items-center justify-center hover:bg-[var(--mm-purple-dark)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button onClick={() => go(index + 1)} aria-label="Next slide" data-testid="netrak-next"
          className="w-10 h-10 rounded-full border border-[var(--mm-line)] bg-white text-[var(--mm-purple)] flex items-center justify-center hover:border-[var(--mm-purple)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
          <ChevronRight className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 ml-1" role="tablist" aria-label="Choose slide">
          {slides.map((s, i) => (
            <button key={s.src} onClick={() => setIndex(i)} role="tab" aria-selected={i === index}
              aria-label={`Slide ${i + 1}: ${s.label}`} data-testid={`netrak-dot-${i}`}
              className={`h-2.5 rounded-full transition-all ${i === index ? 'w-7 bg-[var(--mm-purple)]' : 'w-2.5 bg-[var(--mm-purple)]/25 hover:bg-[var(--mm-purple)]/45'}`} />
          ))}
        </div>
        <span className="text-xs font-semibold text-[var(--mm-purple)] ml-1">{index + 1}/{count}</span>
        <button onClick={openLightbox} data-testid="netrak-enlarge"
          className="ml-auto inline-flex items-center gap-2 text-xs font-semibold text-[var(--mm-purple)] border border-[var(--mm-line)] hover:border-[var(--mm-purple)] rounded-full px-4 py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mm-purple)]">
          Enlarge to read <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="mt-2 text-xs text-[var(--mm-ink)]/50 sm:hidden">Tap a slide to open a larger, zoomable view.</p>

      {lightbox && createPortal((
        <div className="fixed inset-0 z-[9999] flex flex-col" role="dialog" aria-modal="true"
          style={{ backgroundColor: '#1c0722' }}
          aria-label={current.label} data-testid="netrak-lightbox">
          <div className="flex items-center justify-between gap-4 px-4 md:px-6 py-3 text-white">
            <p className="text-sm font-semibold truncate">{index + 1}/{count} · {current.label}</p>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setZoomed((z) => !z)} data-testid="netrak-lightbox-zoom"
                aria-label={zoomed ? 'Fit slide to screen' : 'Zoom in to actual size'}
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
              </button>
              <button onClick={() => setLightbox(false)} data-testid="netrak-lightbox-close" aria-label="Close"
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto flex items-center justify-center p-3 md:p-6" style={{ touchAction: 'pinch-zoom' }}
            data-testid="netrak-lightbox-scroll">
            <img
              src={current.src}
              alt={current.alt}
              onClick={() => setZoomed((z) => !z)}
              className={zoomed
                ? 'max-w-none w-[1600px] max-h-none cursor-zoom-out'
                : 'max-w-full max-h-full object-contain cursor-zoom-in'}
            />
          </div>

          <div className="flex items-center justify-center gap-4 px-4 py-4">
            <button onClick={() => go(index - 1)} aria-label="Previous slide" data-testid="netrak-lightbox-prev"
              className="w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              {slides.map((s, i) => (
                <button key={s.src} onClick={() => setIndex(i)} aria-label={`Slide ${i + 1}`} data-testid={`netrak-lightbox-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all ${i === index ? 'w-8 bg-white' : 'w-2.5 bg-white/40 hover:bg-white/70'}`} />
              ))}
            </div>
            <button onClick={() => go(index + 1)} aria-label="Next slide" data-testid="netrak-lightbox-next"
              className="w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[var(--mm-purple)] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
